/**
 * Plano 9 / A9-05 — Provas de utilidade do Sentinela.
 * Card que mostra evidência operacional: últimas propostas aceitas/aplicadas
 * e últimos rollbacks executados nas últimas 7 dias. Se ambos zerarem, o
 * Sentinela está placebo — o card sinaliza explicitamente.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, RotateCcw, AlertTriangle, ShieldCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

export function SentinelUtilityProof() {
  const proposalsQ = useQuery({
    queryKey: ["sentinel", "proposals", "applied", "7d"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await (supabase as any)
        .from("sentinel_proposals")
        .select("id, title, status, applied_at, created_at, confidence")
        .in("status", ["applied", "accepted", "auto_applied"])
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(10);
      return data ?? [];
    },
    refetchInterval: 60_000,
  });

  const rollbacksQ = useQuery({
    queryKey: ["sentinel", "rollbacks", "7d"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await (supabase as any)
        .from("sentinel_rollback_events")
        .select("id, metric_name, sigma_distance, action_taken, action_result, detected_at")
        .gte("detected_at", since)
        .order("detected_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
    refetchInterval: 60_000,
  });

  const isLoading = proposalsQ.isLoading || rollbacksQ.isLoading;
  const proposals = proposalsQ.data ?? [];
  const rollbacks = rollbacksQ.data ?? [];
  const hasUtility = proposals.length > 0 || rollbacks.length > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {hasUtility ? (
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          )}
          Provas de utilidade — últimos 7 dias
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-32" />
        ) : !hasUtility ? (
          <div className="text-sm text-amber-600 dark:text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-md p-3">
            <strong>Sentinela operacional, mas sem prova de utilidade.</strong>
            <p className="text-xs mt-1">
              Nenhuma proposta aplicada e nenhum rollback executado nas últimas 7 dias.
              Verifique se <code>sentinel-auto-apply</code> está ativo para propostas
              de baixo impacto e se o <code>rollback-monitor</code> está rodando.
            </p>
          </div>
        ) : (
          <>
            <section>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Propostas aplicadas ({proposals.length})
              </div>
              <ul className="space-y-1.5">
                {proposals.slice(0, 5).map((p: any) => (
                  <li key={p.id} className="flex items-center justify-between text-sm border-b border-border/40 pb-1">
                    <span className="truncate flex-1">{p.title || p.id.slice(0, 8)}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
                      {typeof p.confidence === "number" && (
                        <Badge variant="outline" className="text-[10px]">conf {p.confidence.toFixed(2)}</Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(p.applied_at || p.created_at), { addSuffix: true, locale: ptBR })}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-2">
                <RotateCcw className="w-3.5 h-3.5" />
                Rollbacks executados ({rollbacks.length})
              </div>
              {rollbacks.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum rollback necessário (saudável).</p>
              ) : (
                <ul className="space-y-1.5">
                  {rollbacks.map((r: any) => (
                    <li key={r.id} className="flex items-center justify-between text-sm border-b border-border/40 pb-1">
                      <span className="truncate flex-1">{r.metric_name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant={r.action_result === "success" ? "default" : "destructive"} className="text-[10px]">
                          {r.action_taken}
                        </Badge>
                        {typeof r.sigma_distance === "number" && (
                          <Badge variant="outline" className="text-[10px]">σ {r.sigma_distance.toFixed(1)}</Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(r.detected_at), { addSuffix: true, locale: ptBR })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
}
